import { ExternalLink } from 'lucide-react';
import { fmtDay } from './SignPageShell';

const HEAD_FONT = { fontFamily: "'Exo 2', sans-serif" };

export function OpenPdfLink({ href, testId, label = 'Open PDF in new tab' }) {
  return (
    <a href={href} target="_blank" rel="noopener noreferrer" data-testid={testId}
      className="inline-flex items-center gap-1 text-xs font-semibold text-[#4575A8] hover:text-[#5C8AC2] transition-colors">
      {label} <ExternalLink className="w-3.5 h-3.5" />
    </a>
  );
}

/** Who's asking and what you're signing (shared by the iframe page and the D13 placed-fields page). */
export function DocumentSignIntro({ meta }) {
  const company = meta.company?.name || 'The sender';
  const docName = meta.document?.name || 'this document';
  return (
    <section className="space-y-3 animate-in fade-in-0 slide-in-from-bottom-2 duration-300" data-testid="doc-sign-header">
      <h1 className="text-2xl sm:text-3xl font-bold tracking-tight leading-tight break-words" style={HEAD_FONT} data-testid="doc-sign-headline">
        {company} has requested your signature on {docName}
      </h1>
      <div className="flex flex-wrap gap-x-4 gap-y-1 text-sm text-[#5C6370]">
        <span>Signing as <strong className="text-[#111417]" data-testid="doc-sign-signer-name">{meta.signer?.name}</strong></span>
        {meta.sender_name && <span>Sent by <strong className="text-[#111417]">{meta.sender_name}</strong></span>}
      </div>
      {meta.expires_at && <div className="text-xs text-[#8A8F99]" data-testid="doc-sign-expires">This link expires {fmtDay(meta.expires_at)}.</div>}
      {meta.message && (
        <blockquote className="bg-white border border-[#D5D8DD] border-l-4 border-l-[#4575A8] rounded-sm px-4 py-3 text-sm whitespace-pre-line" data-testid="doc-sign-sender-message">
          <div className="text-[10px] font-semibold uppercase tracking-wider text-[#5C6370] mb-1">Note from {meta.sender_name || company}</div>
          {meta.message}
        </blockquote>
      )}
    </section>
  );
}

/** Intro + the PDF itself. No `sandbox`: Chrome disables its PDF viewer in sandboxed frames. */
export function DocumentSignHeader({ meta, pdfUrl }) {
  const docName = meta.document?.name || 'this document';
  return (
    <>
      <DocumentSignIntro meta={meta} />
      <section className="bg-white border border-[#D5D8DD] rounded-sm overflow-hidden">
        <header className="flex items-center justify-between gap-3 px-4 sm:px-6 py-3 border-b border-[#E6E8EC]">
          <div className="min-w-0">
            <div className="text-[10px] font-semibold uppercase tracking-wider text-[#5C6370]">Step 1</div>
            <h2 className="text-base font-bold text-[#111417] truncate" title={docName}>Review {docName}</h2>
          </div>
          <OpenPdfLink href={pdfUrl} testId="doc-sign-open-pdf" />
        </header>
        <iframe title={`Document to sign: ${docName}`} src={pdfUrl} data-testid="doc-sign-frame" className="w-full h-[65vh] sm:h-[75vh] bg-[#E6E8EC]" />
        <div className="px-4 sm:px-6 py-2.5 border-t border-[#E6E8EC] text-xs text-[#5C6370] flex flex-wrap items-center gap-x-2">
          <span>Document not showing?</span>
          <OpenPdfLink href={pdfUrl} testId="doc-sign-open-pdf-fallback" />
        </div>
      </section>
    </>
  );
}

export default DocumentSignHeader;
